import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import type { RootState } from '../index';

type ChatVideoGenerationState = {
	pendingMessageIds: number[];
	tagToMessageIdMap: Record<string, number>;
};

const initialState: ChatVideoGenerationState = {
	pendingMessageIds: [],
	tagToMessageIdMap: {},
};

const chatVideoGenerationSlice = createSlice({
	name: 'chatVideoGeneration',
	initialState,
	reducers: {
		addMessageId: (state, action: PayloadAction<number>) => {
			const id = action.payload;
			if (!state.pendingMessageIds.includes(id)) {
				state.pendingMessageIds.push(id);
			}
		},

		deleteMessageId: (state, action: PayloadAction<number>) => {
			const id = action.payload;
			state.pendingMessageIds = state.pendingMessageIds.filter(
				(messageId) => messageId !== id
			);
		},

		attachTagToLastMessageId: (state, action: PayloadAction<string>) => {
			const tag = action.payload;
			const lastId =
				state.pendingMessageIds[state.pendingMessageIds.length - 1];

			if (lastId !== undefined) {
				state.tagToMessageIdMap[tag] = lastId;
			}
		},

		deleteMessageIdByTag: (state, action: PayloadAction<string>) => {
			const tag = action.payload;
			const messageId = state.tagToMessageIdMap[tag];

			if (messageId !== undefined) {
				state.pendingMessageIds = state.pendingMessageIds.filter(
					(id) => id !== messageId
				);

				delete state.tagToMessageIdMap[tag];
			}
		},
	},
});

const selectChatVideoGeneration = (state: RootState) =>
	state.chatVideoGeneration;

const getIsMessageIdInList = (messageId: number) =>
	createSelector(selectChatVideoGeneration, (state) =>
		state.pendingMessageIds.includes(messageId)
	);

const getMessageIdByTag = (tag: string) =>
	createSelector(
		selectChatVideoGeneration,
		(state) => state.tagToMessageIdMap[tag] ?? null
	);

export const chatVideoGenerationSelectors = {
	getIsMessageIdInList,
	getMessageIdByTag,
};

export const chatVideoGenerationActions = chatVideoGenerationSlice.actions;

export default chatVideoGenerationSlice.reducer;
